import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PartialDeep } from "type-fest";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { PesTest, usePesTestLazyQuery } from "generated/hooks";
import SensorDataCharts from "./SensorDataCharts";
import { Skeleton } from "~/components/ui/skeleton";

interface ComparisonTest {
  sampleFileUrl: string;
  name: string;
}

const ComparePesTests = () => {
  const searchParams = useSearchParams();
  const testIds = searchParams[0].get("tests")?.split(",") ?? [];
  const [pesTest] = usePesTestLazyQuery();
  const [tests, setTests] = useState<
    PartialDeep<PesTest, { recurseIntoArrays: true }>[]
  >([]);

  useEffect(() => {
    Promise.all(
      testIds.map(async (testId) => {
        const { data } = await pesTest({ variables: { id: testId } });
        return data?.pesTest ?? null;
      })
    ).then((fetchedTests) => {
      setTests(
        fetchedTests.map((test, index) => ({
          ...test,
          index,
        }))
      );
    });
  }, [JSON.stringify(testIds)]);

  if (!testIds || testIds.length === 0) {
    return (
      <p className="text-sm font-semibold text-destructive">
        No tests provided for comparison.
      </p>
    );
  }

  const allDataLoaded =
    tests.length === testIds.length &&
    tests.length > 0 &&
    !tests.some((t) => !t?.testType);
  const typesMatch = allDataLoaded
    ? tests.every((t) => t?.testType === tests[0]?.testType)
    : false;
  const hasMissingData = allDataLoaded
    ? tests.some((t) => !t?.sampleFileUrl)
    : false;

  const testsWithUniqueNames = allDataLoaded
    ? tests
        .map((test, index) => ({
          ...test,
          testName:
            tests.filter((t) => t?.name === test?.name).length > 1
              ? `${test?.name ?? `Test ${index + 1}`} (${index + 1})`
              : test?.name,
        }))
        .filter((t) => !!t?.sampleFileUrl)
    : [];

  return (
    <PageTemplate
      title="Compare PES Tests"
      activeItem="Admin"
      activeSubItem="PES Tests"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Test Files", path: "/admin/pes-tests" },
        {
          label: "Compare PES Tests",
          path: `/admin/pes-tests/compare`,
        },
      ]}
    >
      {!allDataLoaded ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {!typesMatch && (
            <p className="text-sm font-semibold text-destructive">
              Warning: You are comparing tests of different types. This is
              probably a mistake!
            </p>
          )}
          {hasMissingData && (
            <p className="text-sm font-semibold text-destructive">
              Warning: One or more tests are missing processed data
            </p>
          )}
        </div>
      )}
      {allDataLoaded && testsWithUniqueNames.length > 0 && (
        <SensorDataCharts tests={testsWithUniqueNames as ComparisonTest[]} />
      )}
    </PageTemplate>
  );
};

export default ComparePesTests;
